import React, { useState, useEffect, createContext, useContext, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { 
  MessageSquare, ShoppingBag, Calendar, Wallet, Heart, Music, 
  Plane, FileText, Bell, Settings, LogOut, Menu, X,
  Users, Building2, BarChart3, Shield,
  Send, Mic, MicOff, Plus, Check, Clock, Trash2,
  Package, Wrench, TrendingUp, AlertCircle, List, CheckSquare,
  Key, Webhook, BookOpen, Copy, ExternalLink, Eye, EyeOff,
  RefreshCw, Code, Database, Store, Headphones, MessageCircle,
  Globe, Sliders, ArrowUp, MapPin, Upload, Link, FileUp,
  Layout, Puzzle, Brain, Image, Video, Play, Smile, HelpCircle, Share2, Film,
  Sun, Moon, Car, Navigation, History, Sparkles, Home, Star, User, ChevronRight, ArrowLeft, Download,
  CreditCard, AlertTriangle, Zap, Cpu, Plug
} from "lucide-react";
import "./App.css";
import { LanguageProvider, useLanguage } from "./contexts/LanguageContext";
import LanguagePicker from "./components/LanguagePicker";

// Import modules
import SettingsModule from "./components/SettingsModule";
import FinanceModule from "./components/FinanceModule";
import HealthModule from "./components/HealthModule";
import TravelModule from "./components/TravelModule";
import MusicModule from "./components/MusicModule";
import MarketplaceModule from "./components/MarketplaceModule";
import CalendarModule from "./components/CalendarModule";
import NotesModule from "./components/NotesModule";
import RemindersModule from "./components/RemindersModule";
import ListsModule from "./components/ListsModule";
import DocumentsModule from "./components/DocumentsModule";
import MemesModule from "./components/MemesModule";
import SupportModule from "./components/SupportModule";
import BusinessDashboardFase7 from "./components/BusinessDashboard";
import ShareModal from "./components/ShareModal";
import ChatHistory from "./components/ChatHistory";
import RichContent from "./components/RichContent";
import ChatModule from "./components/ChatModule";
import SuperPoderesPage from "./components/SuperPoderesPage";
import AdminSupportPanel from "./components/AdminSupportPanel";
import OAuthSSOSection from "./components/OAuthSSOSection";
import OkGoIntegrationPanel from "./components/OkGoIntegrationPanel";
import SuperAdminConnect from "./components/SuperAdminConnect";
import AIAgentsManager from "./components/AIAgentsManager";
import ExternalIntegrationsPanel from "./components/ExternalIntegrationsPanel";
import KnowledgeAPIsManager from "./components/KnowledgeAPIsManager";
import WebScraperTool from "./components/WebScraperTool";
import RapidAPIManager from "./components/RapidAPIManager";
import WebhooksManager from "./components/WebhooksManager";
import APIKeysDocsManager from "./components/APIKeysDocsManager";
import WidgetsManager from "./components/WidgetsManager";
import StripeAdminPanel from "./components/StripeAdminPanel";
import MoviesModule from "./components/MoviesModule";
import TermsPage from "./components/TermsPage";
import PrivacyPage from "./components/PrivacyPage";
import LegalNoticePage from "./components/LegalNoticePage";
import CookiesPage from "./components/CookiesPage";
import LegalSection from "./components/LegalSection";
import MCPManager from "./components/MCPManager";
import KapsoServerPanel from "./components/KapsoServerPanel";
import JelouPanel from "./components/JelouPanel";
import AgentEmbedPage from "./components/AgentEmbedPage";
import SoportePage from "./components/SoportePage";
import SpotifyCallback from "./components/SpotifyCallback";
import PublicAPIDocsPage from "./components/PublicAPIDocsPage";
import IntegrationsModule from "./components/IntegrationsModule";
import IntelligenceModule from "./components/IntelligenceModule";
import OkWalletModule from "./components/OkWalletModule";
import OkWalletAdmin from "./components/OkWalletAdmin";
import OkHomeModule from "./components/OkHomeModule";
import OkBookModule from "./components/OkBookModule";
import FishAudioAdminPanel from "./components/FishAudioAdminPanel";
import OkbotAdminPanel from "./components/OkbotAdminPanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const getApiErrorMessage = (error, fallback = "No fue posible cargar la información.") => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail?.message) return detail.message;
  return fallback;
};

// ==================== CONTEXTS ====================
const AuthContext = createContext(null);
const useAuth = () => useContext(AuthContext);

// embedAuth v3 — persistencia cross-iframe / cross-tab / cross-subdomain (5 capas)
import {
  getUser as embedGetUser,
  setSession as embedSetSession,
  clearSession as embedClearSession,
  subscribe as embedSubscribe,
  hydrateFromBackend,
} from "./utils/embedAuth";
import { configureAuthenticatedRequests } from "./utils/httpAuth";

configureAuthenticatedRequests();

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => embedGetUser());

  useEffect(() => {
    // Capa 4: suscripción en tiempo real (BroadcastChannel + storage event)
    const unsub = embedSubscribe((u) => setUser(u));

    // Capa 5: hidratación desde backend si no hay sesión local
    // (cubre el caso donde localStorage/IDB están bloqueados por Safari ITP
    //  pero la cookie .okgo.mx sigue disponible para el backend)
    if (!embedGetUser()) {
      hydrateFromBackend().then((u) => { if (u) setUser(u); });
    }

    return unsub;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = (userData) => {
    const token = userData?.token || localStorage.getItem("okbot_admin_token") || null;
    embedSetSession({ user: userData, token });
    setUser(userData);
  };

  const logout = () => {
    embedClearSession();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// ==================== THEME CONTEXT ====================
const ThemeContext = createContext();

const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    // Check localStorage first, default to 'light' if not set
    const savedTheme = localStorage.getItem('theme');
    return savedTheme || 'light';
  });

  useEffect(() => {
    // Save to localStorage
    localStorage.setItem('theme', theme);
    
    // Apply theme class to document
    if (theme === 'light') {
      document.documentElement.classList.add('theme-light');
      document.body.classList.add('theme-light');
    } else {
      document.documentElement.classList.remove('theme-light');
      document.body.classList.remove('theme-light');
    }
  }, [theme]);

  const toggleTheme = () => setTheme(t => t === 'dark' ? 'light' : 'dark');

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

const useTheme = () => useContext(ThemeContext);

// ==================== PIN KEYPAD - APPLE STYLE ====================
const PinKeypad = ({ onComplete, title, subtitle }) => {
  const [pin, setPin] = useState("");

  const handleKey = (key) => {
    if (pin.length < 4) {
      const newPin = pin + key;
      setPin(newPin);
      if (newPin.length === 4) {
        onComplete(newPin);
        setTimeout(() => setPin(""), 500);
      }
    }
  };

  return (
    <div className="flex flex-col items-center">
      <h2 className="text-2xl font-semibold text-white mb-2">{title}</h2>
      {subtitle && <p className="text-gray-400 text-sm mb-10">{subtitle}</p>}
      
      <div className="flex gap-5 mb-12">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className={`pin-dot ${pin.length > i ? 'filled' : ''}`} />
        ))}
      </div>

      <div className="grid grid-cols-3 gap-4">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
          <button key={num} className="pin-key" onClick={() => handleKey(String(num))}>
            {num}
          </button>
        ))}
        <button className="pin-key text-gray-500" onClick={() => setPin("")}>C</button>
        <button className="pin-key" onClick={() => handleKey("0")}>0</button>
        <button className="pin-key text-gray-500" onClick={() => setPin(pin.slice(0, -1))}>
          <X size={20} />
        </button>
      </div>
    </div>
  );
};

// ==================== SSO LOGIN BUTTONS (mostrados en la página de login) ======
const SSOLoginButtons = () => {
  const [providers, setProviders] = React.useState([]);

  React.useEffect(() => {
    axios.get(`${API}/oauth/providers`)
      .then(r => setProviders((r.data || []).filter(p => p.enabled)))
      .catch(() => {});
  }, []);

  if (providers.length === 0) return null;

  const initiateSSO = async (providerType) => {
    try {
      const res = await axios.get(`${API}/oauth/sso/initiate/${providerType}`);
      window.location.href = res.data.auth_url;
    } catch {
      toast.error('Error al conectar con el proveedor SSO');
    }
  };

  const icons = {
    bubble: (
      <svg viewBox="0 0 20 20" className="w-5 h-5 flex-shrink-0">
        <circle cx="10" cy="10" r="9" fill="#0D1117" />
        <circle cx="10" cy="10" r="6" fill="#1a73e8" />
        <circle cx="10" cy="10" r="3" fill="white" />
      </svg>
    ),
    google: <Globe size={18} className="text-red-400 flex-shrink-0" />,
    microsoft: <Shield size={18} className="text-blue-400 flex-shrink-0" />,
  };

  const labels = { bubble: 'Bubble.io', google: 'Google', microsoft: 'Microsoft' };

  return (
    <div className="mt-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="flex-1 h-px bg-white/10" />
        <span className="text-gray-600 text-xs uppercase tracking-wider">o continúa con</span>
        <div className="flex-1 h-px bg-white/10" />
      </div>
      <div className="space-y-2">
        {providers.map(p => (
          <button
            key={p.id}
            onClick={() => initiateSSO(p.provider_type)}
            className="w-full flex items-center justify-center gap-2.5 py-3 px-4 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-purple-500/40 rounded-xl text-white text-sm font-medium transition-all"
          >
            {icons[p.provider_type] || <Key size={18} className="text-gray-400 flex-shrink-0" />}
            Entrar con {labels[p.provider_type] || p.name}
          </button>
        ))}
      </div>
    </div>
  );
};

// ==================== SSO CALLBACK PAGE =======================================
const SSOCallbackPage = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = React.useState('processing');
  const [message, setMessage] = React.useState('Verificando tu identidad...');

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ssoToken = params.get('sso_token');
    const ssoError = params.get('sso_error');

    if (ssoError) {
      const msgs = {
        token_exchange_failed: 'Error al obtener el token del proveedor',
        userinfo_failed: 'No se pudo obtener tu perfil del proveedor',
        no_user_identity: 'El proveedor no compartió información de identidad',
        network_error: 'Error de conexión con el proveedor',
        provider_not_configured: 'Proveedor SSO no configurado',
        no_code: 'Código de autorización no recibido',
      };
      setStatus('error');
      setMessage(msgs[ssoError] || `Error SSO: ${ssoError}`);
      setTimeout(() => navigate('/'), 4000);
      return;
    }

    if (!ssoToken) {
      setStatus('error');
      setMessage('Token SSO no encontrado');
      setTimeout(() => navigate('/'), 3000);
      return;
    }

    axios.get(`${API}/oauth/sso/validate/${ssoToken}`)
      .then(res => {
        if (res.data.success) {
          const u = res.data.user;
          login({ id: u.id, name: u.name, okuser: u.okuser, email: u.email, role: u.role || 'user', oauth_provider: u.oauth_provider });
          toast.success(`Bienvenido, ${u.name || u.okuser}`);
          setStatus('success');
          setMessage(`Sesión iniciada como ${u.name || u.okuser}`);
          setTimeout(() => navigate('/dashboard'), 1200);
        } else {
          throw new Error('Token inválido');
        }
      })
      .catch(err => {
        const detail = err.response?.data?.detail || 'Error al validar la sesión SSO';
        setStatus('error');
        setMessage(typeof detail === 'string' ? detail : 'Error al validar la sesión SSO');
        setTimeout(() => navigate('/'), 4000);
      });
  }, [login, navigate]);

  const icons = { processing: <RefreshCw size={32} className="text-purple-400 animate-spin" />, success: <Check size={32} className="text-green-400" />, error: <AlertCircle size={32} className="text-red-400" /> };
  const colors = { processing: 'text-purple-400', success: 'text-green-400', error: 'text-red-400' };

  return (
    <div className="min-h-screen flex items-center justify-center bg-void relative overflow-hidden login-dark-override">
      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-black to-gray-900" />
      <div className="relative z-10 glass-panel p-12 w-full max-w-sm text-center" style={{ background: 'rgba(26,26,46,0.95)', backdropFilter: 'blur(20px)' }}>
        <img src="/okbot_dark_theme.png" alt="OkBot" className="w-20 h-20 object-contain mx-auto mb-6" />
        <div className="flex justify-center mb-5">{icons[status]}</div>
        <h2 className="text-white font-semibold text-lg mb-2">
          {status === 'processing' ? 'Iniciando sesión' : status === 'success' ? '¡Listo!' : 'Error de autenticación'}
        </h2>
        <p className={`text-sm ${colors[status]}`}>{message}</p>
        {status === 'error' && (
          <button onClick={() => navigate('/')} className="mt-6 text-gray-500 text-sm hover:text-red-400 transition-colors">
            ← Volver al inicio
          </button>
        )}
      </div>
    </div>
  );
};

// ==================== USER LOGIN PAGE ====================
const UserLoginPage = () => {
  const [step, setStep] = useState("okuser");
  const [okuser, setOkuser] = useState("");
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { login } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();

  const handleOkuserSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    
    try {
      const response = await axios.get(`${API}/auth/check-user/${okuser}`);
      if (response.data.exists) {
        setUserData(response.data.data);
        setStep(response.data.data.pin_configured ? "pin" : "setup");
      } else {
        setError("Usuario no encontrado");
      }
    } catch {
      setError("Error de conexión");
    } finally {
      setLoading(false);
    }
  };

  const handlePinLogin = async (pin) => {
    setLoading(true);
    setError("");
    
    try {
      const response = await axios.post(`${API}/auth/login`, { user_id: userData.id, pin });
      if (response.data.success) {
        toast.success("Bienvenido");
        login({ id: response.data.user_id, name: response.data.name, role: response.data.role, token: response.data.token });
        navigate("/dashboard");
      } else {
        setError(response.data.message);
        if (response.data.attempts_remaining !== undefined) {
          toast.error(`${response.data.attempts_remaining} intentos restantes`);
        }
      }
    } catch (err) {
      setError(err.response?.status === 403 ? "Cuenta bloqueada" : "Error");
    } finally {
      setLoading(false);
    }
  };

  const handlePinSetup = async (pin) => {
    setLoading(true);
    try {
      await axios.post(`${API}/auth/setup-pin`, { user_id: userData.id, pin });
      toast.success("PIN configurado");
      setStep("pin");
    } catch {
      setError("Error al configurar PIN");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-void relative overflow-hidden login-dark-override">
      {/* Subtle gradient background - Always dark for login */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-black to-gray-900" />
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-red-500/5 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-red-500/5 rounded-full blur-3xl" />
      
      <div className="relative z-10 glass-panel p-12 w-full max-w-md animate-fade-in-up" style={{background: 'rgba(26, 26, 46, 0.95)', backdropFilter: 'blur(20px)'}}>
        {/* Logo - Always use dark theme logo for login */}
        <div className="flex flex-col items-center mb-10">
          <img 
            src="/okbot_dark_theme.png"
            alt="OkBot" 
            className="w-28 h-28 object-contain mb-2"
          />
          <p className="text-gray-400 text-sm mt-1">Tu asistente personal</p>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6 flex items-center gap-3 animate-fade-in">
            <AlertCircle className="text-red-400 w-5 h-5 flex-shrink-0" />
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {step === "okuser" && (
          <form onSubmit={handleOkuserSubmit} className="space-y-6">
            <div>
              <label className="block text-gray-400 text-sm mb-2 font-medium">Nombre / OkUser</label>
              <input type="text" value={okuser} onChange={(e) => setOkuser(e.target.value)}
                     className="input-glass w-full text-lg" placeholder="tu_okuser" required autoFocus />
            </div>
            <button type="submit" disabled={loading} className="btn-primary w-full text-base">
              {loading ? "Verificando..." : "Continuar"}
            </button>
          </form>
        )}

        {step === "pin" && <PinKeypad onComplete={handlePinLogin} title="Ingresa tu PIN" subtitle={`Hola, ${userData?.name}`} />}
        {step === "setup" && <PinKeypad onComplete={handlePinSetup} title="Crea tu PIN" subtitle="4 dígitos" />}

        {step !== "okuser" && (
          <button onClick={() => { setStep("okuser"); setError(""); }}
                  className="mt-8 text-gray-500 text-sm hover:text-red-400 transition-colors w-full text-center">
            Usar otro usuario
          </button>
        )}

        <div className="mt-6 pt-6 border-t border-white/5 flex justify-center text-sm">
          <a href="/oknegocios" className="text-gray-500 hover:text-red-400 transition-colors flex items-center gap-2">
            <Store size={16} />Acceso Negocios
          </a>
        </div>

        {/* Super Poderes Link */}
        <a href="/superpoderes" className="mt-5 block text-center py-3 px-6 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-xl font-medium transition-all hover:scale-105">
          ⚡ Super Poderes de OkBot
        </a>
      </div>
    </div>
  );
};

// ==================== BUSINESS LOGIN PAGE ====================
const BusinessLoginPage = () => {
  const [step, setStep] = useState("okbusiness");
  const [okbusiness, setOkbusiness] = useState("");
  const [businessData, setBusinessData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();
  const { theme } = useTheme();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    
    try {
      const response = await axios.get(`${API}/auth/check-business/${okbusiness}`);
      if (response.data.exists) {
        setBusinessData(response.data.data);
        setStep(response.data.data.pin_configured ? "pin" : "setup");
      } else {
        setError("Negocio no encontrado");
      }
    } catch {
      setError("Error de conexión");
    } finally {
      setLoading(false);
    }
  };

  const handlePinLogin = async (pin) => {
    setLoading(true);
    setError("");
    
    try {
      const response = await axios.post(`${API}/auth/login`, { user_id: businessData.id, pin });
      if (response.data.success) {
        toast.success("Bienvenido");
        login({ id: response.data.user_id, name: response.data.name, role: "business", token: response.data.token });
        navigate("/business/dashboard");
      } else {
        setError(response.data.message);
      }
    } catch (err) {
      setError(err.response?.status === 403 ? "Cuenta bloqueada" : "Error");
    } finally {
      setLoading(false);
    }
  };

  const handlePinSetup = async (pin) => {
    setLoading(true);
    try {
      await axios.post(`${API}/auth/setup-pin`, { user_id: businessData.id, pin });
      toast.success("PIN configurado");
      setStep("pin");
    } catch {
      setError("Error al configurar PIN");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-void relative overflow-hidden login-dark-override">
      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-black to-gray-900" />
      <div className="absolute top-1/3 right-1/4 w-80 h-80 bg-red-500/5 rounded-full blur-3xl" />
      
      <div className="relative z-10 glass-panel p-12 w-full max-w-md animate-fade-in-up" style={{background: 'rgba(26, 26, 46, 0.95)', backdropFilter: 'blur(20px)'}}>
        <div className="flex flex-col items-center mb-10">
          <img 
            src="/okbot_dark_theme.png"
            alt="OkNegocios" 
            className="w-28 h-28 object-contain mb-2"
          />
          <h1 className="text-3xl font-semibold text-white tracking-tight">OkNegocios</h1>
          <p className="text-gray-400 text-sm mt-1">Portal de negocios</p>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6 flex items-center gap-3">
            <AlertCircle className="text-red-400 w-5 h-5" />
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {step === "okbusiness" && (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-gray-400 text-sm mb-2 font-medium">Nombre del Negocio</label>
              <input type="text" value={okbusiness} onChange={(e) => setOkbusiness(e.target.value)}
                     className="input-glass w-full text-lg" placeholder="mi_negocio" required autoFocus />
            </div>
            <button type="submit" disabled={loading} className="btn-primary w-full text-base">
              {loading ? "Verificando..." : "Continuar"}
            </button>
          </form>
        )}

        {step === "pin" && <PinKeypad onComplete={handlePinLogin} title="Ingresa tu PIN" subtitle={businessData?.name} />}
        {step === "setup" && <PinKeypad onComplete={handlePinSetup} title="Crea tu PIN" subtitle="4 dígitos" />}

        {step !== "okbusiness" && (
          <button onClick={() => { setStep("okbusiness"); setError(""); }}
                  className="mt-8 text-gray-500 text-sm hover:text-red-400 transition-colors w-full text-center">
            Usar otro negocio
          </button>
        )}

        <div className="mt-10 pt-6 border-t border-white/5 text-center">
          <a href="/" className="text-gray-500 text-sm hover:text-red-400 transition-colors">
            Volver al inicio
          </a>
        </div>
      </div>
    </div>
  );
};

// ==================== ADMIN LOGIN ====================
const AdminLoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await axios.post(`${API}/admin/login`, { email, password });
      if (response.data.success) {
        localStorage.setItem("okbot_admin_token", response.data.token);
        login({ id: "admin", name: response.data.name, role: "admin", token: response.data.token });
        toast.success("Bienvenido, Administrador");
        navigate("/admin/dashboard");
      }
    } catch (err) {
      setError(getApiErrorMessage(err, "No fue posible iniciar sesión. Intenta nuevamente."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-void relative overflow-hidden login-dark-override">
      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-black to-gray-900" />
      <div className="glass-panel p-12 w-full max-w-md animate-fade-in-up relative z-10" style={{background: 'rgba(26, 26, 46, 0.95)', backdropFilter: 'blur(20px)'}}>
        <div className="flex flex-col items-center mb-10">
          <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-gray-700 to-gray-800 flex items-center justify-center mb-4">
            <Shield className="w-10 h-10 text-red-400" />
          </div>
          <h1 className="text-3xl font-semibold text-white">Administrador</h1>
          <p className="text-gray-400 text-sm mt-1">Panel de control</p>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6 flex items-center gap-3">
            <AlertCircle className="text-red-400 w-5 h-5" />
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-gray-400 text-sm mb-2 font-medium">Correo</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                   className="input-glass w-full" placeholder="admin@okbot.com" required />
          </div>
          <div>
            <label className="block text-gray-400 text-sm mb-2 font-medium">Contraseña</label>
            <div className="relative">
              <input type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)}
                     className="input-glass w-full pr-12" placeholder="••••••••" required />
              <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white">
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          <button type="submit" disabled={loading} className="btn-primary w-full">
            {loading ? "Verificando..." : "Iniciar Sesión"}
          </button>
        </form>

        <div className="mt-8 pt-6 border-t border-white/5 text-center">
          <a href="/" className="text-gray-500 text-sm hover:text-red-400 transition-colors">Volver</a>
        </div>
      </div>
    </div>
  );
};

// ==================== ERROR BOUNDARY ====================
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error('ErrorBoundary caught:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center p-12 text-center">
          <div className="w-16 h-16 rounded-2xl bg-red-500/15 flex items-center justify-center mb-4">
            <AlertTriangle className="text-red-400" size={28} />
          </div>
          <h3 className="text-white font-semibold text-lg mb-2">Algo salió mal en esta sección</h3>
          <p className="text-gray-500 text-sm mb-5 max-w-md">
            Ocurrió un error inesperado. Puedes intentar recargar la sección o contactar soporte si el problema persiste.
          </p>
          <button onClick={() => this.setState({ hasError: false, error: null })}
            className="btn-primary px-6 py-2.5 text-sm">
            Reintentar
          </button>
          {this.state.error && (
            <p className="text-gray-700 text-xs font-mono mt-4 max-w-lg break-all">
              {this.state.error.message}
            </p>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}

// ==================== CHAT WIDGET - APPLE STYLE ====================
const ChatWidget = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const { theme } = useTheme();

  return (
    <>
      {/* Floating Button */}
      <button onClick={() => setIsOpen(!isOpen)}
              className={`fixed bottom-6 right-6 widget-button z-[9999] ${isOpen ? 'scale-0 opacity-0' : 'scale-100 opacity-100'}`}
              style={{ transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)' }}
              data-testid="chat-widget-btn">
        <MessageCircle size={26} className="text-white" />
      </button>

      {/* Widget Window */}
      <div className={`fixed bottom-6 right-6 w-96 widget-window z-[9999] ${isOpen ? 'animate-scale-in' : 'scale-0 opacity-0 pointer-events-none'}`}
           style={{ transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', maxHeight: '600px' }}>
        {/* Header - Simplified without circular icon */}
        <div className="widget-header">
          <div className="flex items-center gap-3">
            <div>
              <p className="text-white font-medium text-sm">OkBot</p>
              <p className="text-gray-500 text-xs">Asistente IA</p>
            </div>
          </div>
          <button onClick={() => setIsOpen(false)} className="text-gray-500 hover:text-white transition-colors p-2">
            <X size={20} />
          </button>
        </div>

        {/* Chat Module - Refactored Component */}
        <div className="h-[480px] flex flex-col">
          <ChatModule user={user} navigate={navigate} theme={theme} />
        </div>
      </div>
    </>
  );
};

// ==================== SIDEBAR ====================
const Sidebar = ({ activeModule, setActiveModule, isMobile, isOpen, setIsOpen }) => {
  const { user, logout } = useAuth();
  const { theme } = useTheme();
  const { t, currentLang } = useLanguage();
  const navigate = useNavigate();

  const modules = [
    { id: "chat", icon: MessageSquare, label: t('chat') },
    { id: "intelligence", icon: Brain, label: t('intelligence') },
    { id: "marketplace", icon: ShoppingBag, label: t('marketplace') },
    { id: "calendar", icon: Calendar, label: t('calendar') },
    { id: "notes", icon: FileText, label: t('notes') },
    { id: "reminders", icon: Bell, label: t('reminders') },
    { id: "lists", icon: List, label: t('lists') },
    { id: "okwallet", icon: Wallet, label: "OkWallet" },
    { id: "finance", icon: Wallet, label: t('finance') },
    { id: "health", icon: Heart, label: t('health') },
    { id: "travel", icon: Plane, label: t('travel') },
    { id: "music", icon: Music, label: t('music') },
    { id: "documents", icon: FileText, label: t('documents') },
    { id: "memes", icon: Smile, label: t('memes') },
    { id: "movies", icon: Film, label: t('movies') },
    { id: "support", icon: HelpCircle, label: t('support') },
    { id: "integrations", icon: Plug, label: t('integrations') },
    { id: "okhome", icon: Home, label: t('okhome') },
    { id: "okbook", icon: BookOpen, label: t('okbook') },
  ];

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const content = (
    <div className="h-full flex flex-col bg-surface">
      <div className="p-4 flex items-center gap-2">
        <img
          src={theme === 'light' ? '/okbot_light_theme.png' : '/okbot_dark_theme.png'}
          alt="OkBot"
          className="w-36 h-auto object-contain"
        />
      </div>

      <div className="px-5 mb-6">
        <div className="glass-panel-sm p-4">
          <p className="text-white font-medium text-sm">{user?.name}</p>
          <p className="text-gray-500 text-xs capitalize">{user?.role}</p>
        </div>
      </div>

      <nav className="flex-1 px-4 overflow-y-auto custom-scrollbar">
        {modules.map((m) => {
          const Icon = m.icon;
          return (
            <button key={m.id} onClick={() => { setActiveModule(m.id); if (isMobile) setIsOpen(false); }}
                    className={`sidebar-item w-full mb-1 ${activeModule === m.id ? 'active' : ''}`}>
              <Icon size={18} /><span>{m.label}</span>
            </button>
          );
        })}
      </nav>

      <div className="p-4 border-t border-white/5">
        <button onClick={() => setActiveModule("settings")} className="sidebar-item w-full mb-2">
          <Settings size={18} /><span>{t('settings')}</span>
        </button>
        <button onClick={handleLogout} className="sidebar-item w-full text-red-400">
          <LogOut size={18} /><span>{t('logout')}</span>
        </button>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <>
        {isOpen && <div className="fixed inset-0 bg-black/60 z-40 backdrop-blur-sm" onClick={() => setIsOpen(false)} />}
        <aside className={`fixed left-0 top-0 h-full w-72 z-50 transform transition-transform duration-300 ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}>
          {content}
        </aside>
      </>
    );
  }

  return <aside className="w-64 h-screen fixed left-0 top-0 border-r border-white/5">{content}</aside>;
};

// ==================== LIGHTBOX COMPONENT ====================
const Lightbox = ({ src, type, onClose }) => {
  if (!src) return null;
  
  return (
    <div className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-4" onClick={onClose}>
      <button className="absolute top-4 right-4 text-white hover:text-red-400 transition-colors" onClick={onClose}>
        <X size={32} />
      </button>
      {type === "video" ? (
        <video src={src} controls autoPlay className="max-w-full max-h-[90vh] rounded-xl" onClick={(e) => e.stopPropagation()} />
      ) : (
        <img src={src} alt="" className="max-w-full max-h-[90vh] rounded-xl object-contain" onClick={(e) => e.stopPropagation()} />
      )}
    </div>
  );
};

// ==================== RICH MESSAGE RENDERER ====================
const RichMessage = ({ content, onMediaClick }) => {
  // Parse markdown links [text](url)
  const parseLinks = (text) => {
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    
    while ((match = linkRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push({ type: 'text', content: text.slice(lastIndex, match.index) });
      }
      parts.push({ type: 'link', text: match[1], url: match[2] });
      lastIndex = match.index + match[0].length;
    }
    
    if (lastIndex < text.length) {
      parts.push({ type: 'text', content: text.slice(lastIndex) });
    }
    
    return parts.length > 0 ? parts : [{ type: 'text', content: text }];
  };

  // Check for media URLs - improved detection
  const isImageUrl = (url) => {
    // Direct image extensions
    if (/\.(jpg|jpeg|png|gif|webp|svg|bmp)(\?.*)?$/i.test(url)) return true;
    // Common image hosting services
    if (url.includes('picsum.photos')) return true;
    if (url.includes('imgur.com') && !url.includes('/a/')) return true;
    if (url.includes('cloudinary.com') && url.includes('/image/')) return true;
    if (url.includes('images.unsplash.com')) return true;
    if (url.includes('images.pexels.com')) return true;
    if (url.includes('i.redd.it')) return true;
    if (url.includes('media.giphy.com')) return true;
    if (url.includes('placeholder.com')) return true;
    if (url.includes('via.placeholder.com')) return true;
    if (url.includes('loremflickr.com')) return true;
    return false;
  };
  
  const isVideoUrl = (url) => {
    if (/\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(url)) return true;
    if (url.includes('youtube.com') || url.includes('youtu.be')) return true;
    if (url.includes('vimeo.com')) return true;
    return false;
  };
  
  const isMapUrl = (url) => url.includes('maps.google') || url.includes('goo.gl/maps') || url.includes('google.com/maps');

  // Extract bare URLs from text
  const extractBareUrls = (text) => {
    const urlRegex = /(https?:\/\/[^\s<>\[\]()]+)/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    
    while ((match = urlRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push({ type: 'text', content: text.slice(lastIndex, match.index) });
      }
      const url = match[1].replace(/[.,;:!?)]+$/, ''); // Remove trailing punctuation
      parts.push({ type: 'url', url: url });
      lastIndex = match.index + url.length;
    }
    
    if (lastIndex < text.length) {
      parts.push({ type: 'text', content: text.slice(lastIndex) });
    }
    
    return parts.length > 0 ? parts : [{ type: 'text', content: text }];
  };

  // First parse markdown links, then extract bare URLs from remaining text
  const parsedLinks = parseLinks(content);
  const finalParts = [];
  
  parsedLinks.forEach(part => {
    if (part.type === 'text') {
      // Extract bare URLs from text parts
      const urlParts = extractBareUrls(part.content);
      urlParts.forEach(up => {
        if (up.type === 'url') {
          finalParts.push({ type: 'link', text: up.url, url: up.url, isBareUrl: true });
        } else {
          finalParts.push(up);
        }
      });
    } else {
      finalParts.push(part);
    }
  });
  
  return (
    <div className="space-y-2">
      {finalParts.map((part, idx) => {
        if (part.type === 'link') {
          // Image rendering
          if (isImageUrl(part.url)) {
            return (
              <div key={idx} className="mt-2">
                <img 
                  src={part.url} 
                  alt={part.text || 'Imagen'} 
                  className="rounded-lg max-w-[280px] max-h-[200px] object-cover cursor-pointer hover:opacity-80 transition-opacity shadow-lg"
                  onClick={() => onMediaClick(part.url, 'image')}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                {!part.isBareUrl && <p className="text-xs text-gray-500 mt-1">{part.text}</p>}
              </div>
            );
          }
          // YouTube video rendering
          if (isVideoUrl(part.url)) {
            const youtubeMatch = part.url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
            if (youtubeMatch) {
              return (
                <div key={idx} className="mt-2">
                  <div className="relative rounded-lg overflow-hidden max-w-[320px] shadow-lg">
                    <iframe 
                      src={`https://www.youtube.com/embed/${youtubeMatch[1]}`} 
                      className="w-full aspect-video"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                      allowFullScreen 
                    />
                  </div>
                  {!part.isBareUrl && <p className="text-xs text-gray-500 mt-1">{part.text}</p>}
                </div>
              );
            }
            // Direct video file
            if (/\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(part.url)) {
              return (
                <div key={idx} className="mt-2">
                  <video 
                    src={part.url} 
                    controls 
                    className="rounded-lg max-w-[320px] shadow-lg"
                    preload="metadata"
                  >
                    Tu navegador no soporta video
                  </video>
                  {!part.isBareUrl && <p className="text-xs text-gray-500 mt-1">{part.text}</p>}
                </div>
              );
            }
          }
          // Map link rendering
          if (isMapUrl(part.url)) {
            return (
              <a key={idx} href={part.url} target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-2 bg-blue-500/20 text-blue-400 px-3 py-2 rounded-lg hover:bg-blue-500/30 transition-colors">
                <MapPin size={16} />{part.isBareUrl ? 'Ver en Google Maps' : part.text}
              </a>
            );
          }
          // Regular link
          return (
            <a key={idx} href={part.url} target="_blank" rel="noopener noreferrer"
               className="text-red-400 hover:text-red-300 underline underline-offset-2">
              {part.isBareUrl ? part.url.length > 50 ? part.url.slice(0, 50) + '...' : part.url : part.text}
            </a>
          );
        }
        return <span key={idx}>{part.content}</span>;
      })}
    </div>
  );
};


const PlaceholderModule = ({ title, icon: Icon }) => (
  <div className="p-6 flex flex-col items-center justify-center min-h-[60vh]">
    <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-red-500/20 to-red-600/20 flex items-center justify-center mb-6">
      <Icon className="w-10 h-10 text-red-400" />
    </div>
    <h1 className="text-2xl font-semibold text-white mb-2">{title}</h1>
    <p className="text-gray-500">Próximamente</p>
  </div>
);

// ==================== USER DASHBOARD ====================
const Dashboard = () => {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { currentLang, language } = useLanguage();
  const navigate = useNavigate();
  const [activeModule, setActiveModule] = useState("chat");
  const [settingsSection, setSettingsSection] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [langPickerOpen, setLangPickerOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    window.goToSettings = (section) => {
      setSettingsSection(section || null);
      setActiveModule("settings");
    };
    window.openLangPicker = () => setLangPickerOpen(true);
    return () => {
      delete window.goToSettings;
      delete window.openLangPicker;
    };
  }, []);

  const renderModule = () => {
    switch (activeModule) {
      case "chat": return <ChatModule user={user} navigate={navigate} theme={theme} onModuleChange={setActiveModule} />;
      case "notes": return <NotesModule user={user} />;
      case "reminders": return <RemindersModule user={user} />;
      case "finance": return <FinanceModule user={user} />;
      case "health": return <HealthModule user={user} />;
      case "travel": return <TravelModule user={user} />;
      case "music": return <MusicModule user={user} />;
      case "marketplace": return <MarketplaceModule user={user} />;
      case "settings": return <SettingsModule user={user} initialSection={settingsSection} onModuleChange={setActiveModule} />;
      case "calendar": return <CalendarModule user={user} />;
      case "lists": return <ListsModule user={user} />;
      case "documents": return <DocumentsModule user={user} />;
      case "memes": return <MemesModule user={user} />;
      case "movies": return <MoviesModule user={user} />;
      case "support": return <SupportModule user={user} />;
      case "integrations": return <IntegrationsModule user={user} />;
      case "intelligence": return <IntelligenceModule user={user} onModuleChange={setActiveModule} />;
      case "okwallet": return <OkWalletModule user={user} />;
      case "okhome": return <OkHomeModule user={user} />;
      case "okbook": return <OkBookModule user={user} />;
      default: return <PlaceholderModule title={activeModule.charAt(0).toUpperCase() + activeModule.slice(1)} icon={MessageSquare} />;
    }
  };

  return (
    <div className="min-h-screen bg-void">
      {/* Top-right controls: Language + Theme */}
      <div className="fixed top-4 right-4 z-[9998] flex items-center gap-2">
        {/* Language picker button */}
        <button
          onClick={() => setLangPickerOpen(true)}
          className="flex items-center gap-2 px-3 py-2.5 rounded-full bg-white/5 hover:bg-white/10 transition-all border border-white/0 hover:border-white/10"
          title="Cambiar idioma / Change language"
          style={{ backdropFilter: 'blur(12px)' }}
        >
          <span style={{ fontSize: '18px', lineHeight: 1 }}>{currentLang.flag}</span>
          <span style={{ fontSize: '12px', fontWeight: 600, color: 'rgba(255,255,255,0.6)', letterSpacing: '0.3px' }}>
            {currentLang.code.toUpperCase()}
          </span>
        </button>

        {/* Theme toggle */}
        <button
          onClick={toggleTheme}
          className="p-3 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
          title={theme === 'dark' ? 'Modo claro' : 'Modo oscuro'}
          data-testid="theme-toggle"
        >
          {theme === 'dark' ? <Sun size={20} className="text-yellow-400" /> : <Moon size={20} className="text-gray-700" />}
        </button>
      </div>

      {/* Language picker modal */}
      {langPickerOpen && <LanguagePicker onClose={() => setLangPickerOpen(false)} />}

      <Sidebar activeModule={activeModule} setActiveModule={setActiveModule} isMobile={isMobile} isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      
      {isMobile && (
        <header className="fixed top-0 left-0 right-0 h-14 bg-surface/80 backdrop-blur-xl border-b border-white/5 flex items-center px-4 z-30">
          <button onClick={() => setSidebarOpen(true)} className="p-2"><Menu className="w-6 h-6 text-white" /></button>
          <img
            src={theme === 'light' ? '/okbot_light_theme.png' : '/okbot_dark_theme.png'}
            alt="OkBot"
            className="ml-2 h-8 w-auto object-contain"
          />
        </header>
      )}
      
      <main className={`${isMobile ? 'pt-14' : 'ml-64'} min-h-screen`}>{renderModule()}</main>
      <ChatWidget />
    </div>
  );
};

// ==================== BUSINESS DASHBOARD ====================
const BusinessDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-void">
      <header className="bg-surface border-b border-white/5 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-white">{user?.name}</h1>
              <p className="text-gray-500 text-xs">Panel de Negocio</p>
            </div>
          </div>
          <button onClick={() => { logout(); navigate("/oknegocios"); }} className="btn-secondary text-sm py-2 px-4">
            <LogOut size={16} className="mr-2 inline" />Salir
          </button>
        </div>
      </header>

      {/* FASE 7: Full Business Dashboard with Chats, Analytics, Schedule, Settings */}
      <BusinessDashboardFase7 business={{ id: user?.id, name: user?.name, is_active: true }} />

      <ChatWidget />
    </div>
  );
};

// ==================== WIDGETS SECTION COMPONENT ====================
const WidgetsSection = ({ widgets, onDeleteWidget, onGetEmbedCode, selectedWidgetCode, setSelectedWidgetCode, 
  showCreateWidget, setShowCreateWidget, newWidget, setNewWidget, onCreateWidget }) => (
  <div>
    <div className="flex justify-between mb-6">
      <div>
        <h2 className="text-xl font-semibold text-white">Widgets Embebibles</h2>
        <p className="text-gray-500 text-sm mt-1">Genera código para integrar OkBot en cualquier sitio web</p>
      </div>
      <button onClick={() => setShowCreateWidget(true)} className="btn-primary flex items-center gap-2"><Plus size={16} />Nuevo Widget</button>
    </div>

    {showCreateWidget && (
      <div className="glass-panel p-6 mb-6 animate-fade-in-up">
        <h3 className="text-white font-medium mb-4">Crear Widget</h3>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <input type="text" value={newWidget.name} onChange={(e) => setNewWidget({ ...newWidget, name: e.target.value })}
                 placeholder="Nombre del widget" className="input-glass" />
          <select value={newWidget.widget_type} onChange={(e) => setNewWidget({ ...newWidget, widget_type: e.target.value })} className="input-glass">
            <option value="bot_button">Botón del Bot</option>
            <option value="okuser">Ventana OkUser</option>
            <option value="oknegocios">Ventana OkNegocios</option>
          </select>
          <select value={newWidget.theme} onChange={(e) => setNewWidget({ ...newWidget, theme: e.target.value })} className="input-glass">
            <option value="dark">Tema Oscuro</option>
            <option value="light">Tema Claro</option>
          </select>
          <div className="flex items-center gap-2">
            <input type="color" value={newWidget.primary_color} onChange={(e) => setNewWidget({ ...newWidget, primary_color: e.target.value })}
                   className="w-10 h-10 rounded cursor-pointer" />
            <span className="text-gray-400 text-sm">Color primario</span>
          </div>
        </div>
        <input type="text" value={newWidget.welcome_message} onChange={(e) => setNewWidget({ ...newWidget, welcome_message: e.target.value })}
               placeholder="Mensaje de bienvenida" className="input-glass w-full mb-4" />
        <div className="flex gap-3 justify-end">
          <button onClick={() => setShowCreateWidget(false)} className="btn-secondary">Cancelar</button>
          <button onClick={onCreateWidget} className="btn-primary">Crear Widget</button>
        </div>
      </div>
    )}

    {selectedWidgetCode && (
      <div className="glass-panel p-6 mb-6 border-green-500/30 animate-fade-in-up">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-green-400 font-medium">Código de Integración</h3>
          <button onClick={() => setSelectedWidgetCode(null)} className="text-gray-500 hover:text-white"><X size={18} /></button>
        </div>
        <div className="bg-black/40 rounded-lg p-4 mb-4">
          <pre className="text-sm text-gray-300 overflow-x-auto whitespace-pre-wrap">{selectedWidgetCode.embed_code}</pre>
        </div>
        <button onClick={() => { navigator.clipboard.writeText(selectedWidgetCode.embed_code); toast.success("Código copiado"); }}
                className="btn-primary text-sm flex items-center gap-2"><Copy size={14} />Copiar código</button>
      </div>
    )}

    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {widgets.map(w => (
        <div key={w.id} className="glass-panel p-5">
          <div className="flex items-start justify-between mb-3">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: w.primary_color + '20' }}>
              <Layout className="w-6 h-6" style={{ color: w.primary_color }} />
            </div>
            <span className={`badge ${w.is_active ? 'badge-success' : 'badge-error'}`}>{w.is_active ? 'Activo' : 'Inactivo'}</span>
          </div>
          <h4 className="text-white font-medium mb-1">{w.name}</h4>
          <p className="text-gray-500 text-sm mb-3 capitalize">{w.widget_type.replace('_', ' ')}</p>
          <div className="flex gap-2">
            <button onClick={() => onGetEmbedCode(w.id)} className="btn-secondary text-xs flex-1 flex items-center justify-center gap-1">
              <Code size={12} />Código
            </button>
            <button onClick={() => onDeleteWidget(w.id)} className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors">
              <Trash2 size={14} />
            </button>
          </div>
        </div>
      ))}
    </div>

    {widgets.length === 0 && (
      <div className="glass-panel p-12 text-center">
        <Layout className="w-12 h-12 text-gray-600 mx-auto mb-4" />
        <p className="text-gray-500">No hay widgets creados</p>
        <p className="text-gray-600 text-sm mt-2">Crea tu primer widget para integrarlo en cualquier sitio web</p>
      </div>
    )}
  </div>
);

// ==================== BUBBLE SECTION COMPONENT ====================
const BubbleSection = ({ bubbleDocs }) => {
  // Defensive check for undefined data
  if (!bubbleDocs || !bubbleDocs.bubble_integration_guide || !bubbleDocs.endpoints) {
    return (
      <div className="glass-panel p-12 text-center">
        <Puzzle className="w-12 h-12 text-gray-600 mx-auto mb-4" />
        <p className="text-gray-500">Cargando documentación de Bubble.io...</p>
      </div>
    );
  }

  return (
  <div>
    <div className="mb-6">
      <h2 className="text-xl font-semibold text-white">Integración con Bubble.io</h2>
      <p className="text-gray-500 text-sm mt-1">Documentación completa de la API para conectar con Bubble.io</p>
    </div>

    <div className="glass-panel p-6 mb-6">
      <h3 className="text-white font-medium mb-4 flex items-center gap-2">
        <BookOpen className="text-red-400" size={18} />Guía de Integración Rápida
      </h3>
      <div className="space-y-3">
        {Object.entries(bubbleDocs.bubble_integration_guide).filter(([k]) => k.startsWith('step')).map(([key, value]) => (
          <div key={key} className="flex items-start gap-3 p-3 bg-black/20 rounded-lg">
            <span className="w-6 h-6 rounded-full bg-red-500/20 text-red-400 flex items-center justify-center text-xs font-bold">
              {key.replace('step_', '')}
            </span>
            <p className="text-gray-300 text-sm">{value}</p>
          </div>
        ))}
      </div>
      {bubbleDocs.bubble_integration_guide.tips && (
        <div className="mt-4 p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
          <h4 className="text-yellow-400 text-sm font-medium mb-2">Tips importantes:</h4>
          <ul className="space-y-1">
            {bubbleDocs.bubble_integration_guide.tips.map((tip, i) => (
              <li key={i} className="text-gray-400 text-sm flex items-start gap-2">
                <Check size={14} className="text-green-400 mt-0.5" />{tip}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>

    <div className="glass-panel p-6 mb-6">
      <h3 className="text-white font-medium mb-4 flex items-center gap-2">
        <Key className="text-red-400" size={18} />Autenticación
      </h3>
      {bubbleDocs.authentication && (
        <>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 bg-black/20 rounded-lg">
              <p className="text-gray-500 text-xs mb-1">Tipo</p>
              <p className="text-white">{bubbleDocs.authentication.type}</p>
            </div>
            <div className="p-3 bg-black/20 rounded-lg">
              <p className="text-gray-500 text-xs mb-1">Header</p>
              <code className="text-red-400">{bubbleDocs.authentication.header}</code>
            </div>
          </div>
          <p className="text-gray-500 text-sm mt-3">{bubbleDocs.authentication.description}</p>
        </>
      )}
    </div>

    <div className="glass-panel p-6 mb-6">
      <h3 className="text-white font-medium mb-4 flex items-center gap-2">
        <Code className="text-red-400" size={18} />Endpoints ({Object.values(bubbleDocs.endpoints || {}).reduce((acc, cat) => acc + Object.keys(cat || {}).length, 0)})
      </h3>
      <div className="space-y-4 max-h-[400px] overflow-y-auto custom-scrollbar">
        {Object.entries(bubbleDocs.endpoints || {}).map(([category, endpoints]) => (
          <div key={category} className="border border-white/5 rounded-lg overflow-hidden">
            <div className="bg-white/5 px-4 py-2">
              <h4 className="text-white font-medium capitalize">{category}</h4>
            </div>
            <div className="p-4 space-y-2">
              {Object.entries(endpoints || {}).map(([name, details]) => (
                <div key={name} className="flex items-center gap-2 p-2 bg-black/20 rounded">
                  <span className={`text-xs px-2 py-0.5 rounded font-mono ${details.method === 'GET' ? 'bg-green-500/20 text-green-400' : details.method === 'POST' ? 'bg-blue-500/20 text-blue-400' : details.method === 'PUT' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-red-500/20 text-red-400'}`}>
                    {details.method}
                  </span>
                  <code className="text-gray-300 text-sm flex-1">{details.url}</code>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>

    <div className="glass-panel p-6">
      <h3 className="text-white font-medium mb-4 flex items-center gap-2">
        <Webhook className="text-red-400" size={18} />Eventos de Webhook
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {(bubbleDocs.webhook_events || []).map((event, i) => (
          <div key={i} className="p-3 bg-black/20 rounded-lg">
            <code className="text-red-400 text-sm">{event.event}</code>
            <p className="text-gray-500 text-xs mt-1">{event.description}</p>
          </div>
        ))}
      </div>
    </div>
  </div>
  );
};

// ==================== KNOWLEDGE SECTION COMPONENT ====================
const KnowledgeSection = ({ knowledgeSources, onFileUpload, onAddUrl, onDelete, onToggle, uploadingFile, newUrlSource, setNewUrlSource }) => (
  <div>
    <div className="flex justify-between mb-6">
      <div>
        <h2 className="text-xl font-semibold text-white">Fuentes de Conocimiento</h2>
        <p className="text-gray-500 text-sm mt-1">Sube documentos y URLs para enriquecer las respuestas del chatbot</p>
      </div>
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
      <div className="glass-panel p-6">
        <h3 className="text-white font-medium mb-4 flex items-center gap-2">
          <Upload className="text-red-400" size={18} />Subir Documento
        </h3>
        <div className="border-2 border-dashed border-white/10 rounded-xl p-8 text-center hover:border-red-500/50 transition-colors">
          <input type="file" id="file-upload" className="hidden" onChange={onFileUpload}
                 accept=".pdf,.docx,.doc,.xlsx,.xls,.pptx,.ppt,.txt" />
          <label htmlFor="file-upload" className="cursor-pointer">
            <Upload className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 mb-2">Arrastra o haz clic para subir</p>
            <p className="text-gray-600 text-sm">PDF, Word, Excel, PowerPoint, TXT</p>
            {uploadingFile && <p className="text-red-400 mt-2 text-sm">Subiendo...</p>}
          </label>
        </div>
      </div>

      <div className="glass-panel p-6">
        <h3 className="text-white font-medium mb-4 flex items-center gap-2">
          <Link className="text-red-400" size={18} />Agregar URL
        </h3>
        <input type="text" value={newUrlSource.name} onChange={(e) => setNewUrlSource({ ...newUrlSource, name: e.target.value })}
               placeholder="Nombre de la fuente" className="input-glass w-full mb-3" />
        <input type="url" value={newUrlSource.url} onChange={(e) => setNewUrlSource({ ...newUrlSource, url: e.target.value })}
               placeholder="https://ejemplo.com/pagina" className="input-glass w-full mb-4" />
        <button onClick={onAddUrl} disabled={!newUrlSource.url || !newUrlSource.name}
                className="btn-primary w-full flex items-center justify-center gap-2">
          <Plus size={16} />Agregar URL
        </button>
      </div>
    </div>

    <div className="glass-panel p-6">
      <h3 className="text-white font-medium mb-4">Fuentes Activas ({knowledgeSources.length})</h3>
      
      {knowledgeSources.length === 0 ? (
        <div className="text-center py-8">
          <Brain className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-500">No hay fuentes de conocimiento</p>
          <p className="text-gray-600 text-sm mt-2">Sube documentos o agrega URLs para mejorar las respuestas</p>
        </div>
      ) : (
        <div className="space-y-3">
          {knowledgeSources.map(source => (
            <div key={source.id} className="p-4 bg-black/20 rounded-lg flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${source.source_type === 'url' ? 'bg-blue-500/20' : 'bg-red-500/20'}`}>
                  {source.source_type === 'url' ? <Globe className="w-5 h-5 text-blue-400" /> : <FileText className="w-5 h-5 text-red-400" />}
                </div>
                <div>
                  <p className="text-white font-medium">{source.name}</p>
                  <p className="text-gray-500 text-sm">
                    {source.source_type === 'url' ? source.url?.substring(0, 40) + '...' : source.file_type?.toUpperCase()}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => onToggle(source.id)} className={`badge cursor-pointer ${source.is_active ? 'badge-success' : 'badge-error'}`}>
                  {source.is_active ? 'Activo' : 'Inactivo'}
                </button>
                <button onClick={() => onDelete(source.id)} className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
);


// ==================== ADMIN DASHBOARD ====================
const AdminDashboard = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { currentLang } = useLanguage();
  const [langPickerOpen, setLangPickerOpen] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const [users, setUsers] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [apiKeys, setApiKeys] = useState([]);
  const [widgets, setWidgets] = useState([]);
  const [knowledgeSources, setKnowledgeSources] = useState([]);
  const [bubbleDocs, setBubbleDocs] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showCreateBusiness, setShowCreateBusiness] = useState(false);
  const [showCreateWebhook, setShowCreateWebhook] = useState(false);
  const [showCreateWidget, setShowCreateWidget] = useState(false);
  const [newUser, setNewUser] = useState({ name: "", first_name: "", last_name: "", okuser: "", email: "", whatsapp: "", role: "user" });
  const [newBusiness, setNewBusiness] = useState({ name: "", okbusiness: "", email: "", category: "store" });
  const [newWebhook, setNewWebhook] = useState({ name: "", url: "", events: [] });
  const [newWidget, setNewWidget] = useState({ name: "", widget_type: "bot_button", theme: "dark", primary_color: "#E53935", welcome_message: "¡Hola! ¿En qué puedo ayudarte?" });
  const [createdApiKey, setCreatedApiKey] = useState(null);
  const [selectedWidgetCode, setSelectedWidgetCode] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [newUrlSource, setNewUrlSource] = useState({ url: "", name: "" });
  const [dbPersistenceWarning, setDbPersistenceWarning] = useState(null);
  const [dashboardError, setDashboardError] = useState(null);
  const [dataLoading, setDataLoading] = useState(true);

  const webhookEvents = ["user.created", "business.created", "order.created", "order.updated", "payment.completed"];

  useEffect(() => {
    // Check DB persistence status
    axios.get(`${API}/diagnostic`).then(r => {
      if (r.data?.persistence && !r.data.persistence.is_persistent) {
        setDbPersistenceWarning(r.data.persistence.warning);
      }
    }).catch(() => {});
    loadData();
  }, []);

  const loadData = async () => {
    setDataLoading(true);
    const results = await Promise.allSettled([
      axios.get(`${API}/admin/metrics`),
      axios.get(`${API}/admin/users`),
      axios.get(`${API}/admin/businesses`),
      axios.get(`${API}/widgets`),
      axios.get(`${API}/knowledge-sources`),
      axios.get(`${API}/admin/bubble/documentation`)
    ]);
    const [m, u, b, wg, ks, bd] = results;

    if (m.status === "fulfilled") setMetrics(m.value.data);
    if (u.status === "fulfilled") setUsers(u.value.data);
    if (b.status === "fulfilled") setBusinesses(b.value.data);
    if (wg.status === "fulfilled") setWidgets(wg.value.data);
    if (ks.status === "fulfilled") setKnowledgeSources(ks.value.data);
    if (bd.status === "fulfilled") setBubbleDocs(bd.value.data);

    const failedRequest = results.find((result) => result.status === "rejected");
    setDashboardError(
      failedRequest
        ? getApiErrorMessage(failedRequest.reason, "Algunos paneles no pudieron cargar. Reintenta en unos segundos.")
        : null
    );
    setDataLoading(false);
  };

  const createUser = async () => {
    if (!newUser.name || !newUser.okuser) return;
    try {
      await axios.post(`${API}/auth/register-user`, newUser);
      toast.success("Usuario creado");
      setNewUser({ name: "", first_name: "", last_name: "", okuser: "", email: "", whatsapp: "", role: "user" });
      setShowCreateUser(false);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const createBusiness = async () => {
    if (!newBusiness.name || !newBusiness.okbusiness) return;
    try {
      await axios.post(`${API}/auth/register-business`, newBusiness);
      toast.success("Negocio creado");
      setNewBusiness({ name: "", okbusiness: "", email: "", category: "store" });
      setShowCreateBusiness(false);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const createWebhook = async () => {
    if (!newWebhook.name || !newWebhook.url || newWebhook.events.length === 0) return;
    await axios.post(`${API}/webhooks`, newWebhook);
    toast.success("Webhook creado");
    setNewWebhook({ name: "", url: "", events: [] });
    setShowCreateWebhook(false);
    loadData();
  };

  const createWidget = async () => {
    if (!newWidget.name) return;
    const response = await axios.post(`${API}/widgets`, newWidget);
    toast.success("Widget creado");
    setNewWidget({ name: "", widget_type: "bot_button", theme: "dark", primary_color: "#E53935", welcome_message: "¡Hola! ¿En qué puedo ayudarte?" });
    setShowCreateWidget(false);
    loadData();
    // Get embed code
    const codeResponse = await axios.get(`${API}/widgets/${response.data.id}/embed-code`);
    setSelectedWidgetCode(codeResponse.data);
  };

  const getWidgetEmbedCode = async (widgetId) => {
    const response = await axios.get(`${API}/widgets/${widgetId}/embed-code`);
    setSelectedWidgetCode(response.data);
  };

  const deleteWidget = async (widgetId) => {
    await axios.delete(`${API}/widgets/${widgetId}`);
    toast.success("Widget eliminado");
    loadData();
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingFile(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', file.name);
    formData.append('description', '');
    
    try {
      await axios.post(`${API}/knowledge-sources/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success("Documento subido exitosamente");
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al subir archivo");
    } finally {
      setUploadingFile(false);
    }
  };

  const addUrlSource = async () => {
    if (!newUrlSource.url || !newUrlSource.name) return;
    try {
      await axios.post(`${API}/knowledge-sources/url?url=${encodeURIComponent(newUrlSource.url)}&name=${encodeURIComponent(newUrlSource.name)}`);
      toast.success("URL agregada");
      setNewUrlSource({ url: "", name: "" });
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al agregar URL");
    }
  };

  const deleteKnowledgeSource = async (sourceId) => {
    await axios.delete(`${API}/knowledge-sources/${sourceId}`);
    toast.success("Fuente eliminada");
    loadData();
  };

  const toggleKnowledgeSource = async (sourceId) => {
    await axios.put(`${API}/knowledge-sources/${sourceId}/toggle`);
    loadData();
  };

  const createApiKey = async () => {
    const response = await axios.post(`${API}/api-keys`, { name: "API Key", permissions: ["read", "write"] });
    setCreatedApiKey(response.data.key);
    toast.success("API Key creada");
    loadData();
  };

  const generatePin = async (userId) => {
    const r = await axios.post(`${API}/auth/generate-provisional-pin`, { user_id: userId });
    toast.success(`PIN: ${r.data.provisional_pin}`);
  };

  const deleteUser = async (userId, name) => {
    if (!window.confirm(`¿Eliminar al usuario "${name}" permanentemente? Esta acción no se puede deshacer.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success(`Usuario "${name}" eliminado`);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al eliminar"); }
  };

  const deleteBusiness = async (bizId, name) => {
    if (!window.confirm(`¿Eliminar el negocio "${name}" permanentemente? Esta acción no se puede deshacer.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${bizId}`);
      toast.success(`Negocio "${name}" eliminado`);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al eliminar"); }
  };

  const tabs = [
    { id: "overview", label: "Vista General", icon: BarChart3 },
    { id: "users", label: "Usuarios", icon: Users },
    { id: "businesses", label: "Negocios", icon: Building2 },
    { id: "okwallet", label: "OkWallet", icon: CreditCard },
    { id: "support", label: "Soporte", icon: Headphones },
    { id: "stripe", label: "Stripe Billing", icon: CreditCard },
    { id: "connect", label: "Super Admin Connect", icon: Link },
    { id: "agents", label: "Agentes IA", icon: Brain },
    { id: "integrations", label: "Integraciones", icon: Puzzle },
    { id: "knowledge-apis", label: "APIs Conocimiento", icon: Globe },
    { id: "web-scraper", label: "Web Scraper IA", icon: Globe },
    { id: "rapidapi", label: "RapidAPI Hub", icon: Zap },
    { id: "oauth", label: "OAuth SSO", icon: Key },
    { id: "okgo", label: "Integración OkGo", icon: Key },
    { id: "widgets", label: "Widgets", icon: Layout },
    { id: "bubble", label: "Bubble.io", icon: Puzzle },
    { id: "webhooks", label: "Webhooks", icon: Webhook },
    { id: "api", label: "API", icon: Code },
    { id: "legal", label: "Legal", icon: Shield },
    { id: "mcp", label: "MCP Server", icon: Cpu },
    { id: "kapso", label: "Kapso Server", icon: MessageSquare },
    { id: "jelou", label: "Jelou", icon: Zap },
    { id: "fishaudio", label: "🐟 Fish Audio", icon: Zap },
    { id: "okbook", label: "📚 OkBook Admin", icon: BookOpen },
  ];

  return (
    <div className="min-h-screen bg-void">
      <header className="bg-surface border-b border-white/5 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gray-800 flex items-center justify-center">
            <Shield className="w-5 h-5 text-red-400" />
          </div>
          <h1 className="text-lg font-semibold text-white">Administrador</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setLangPickerOpen(true)}
            className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/5 hover:bg-white/10 transition-colors border border-white/5"
            title="Cambiar idioma / Change language"
          >
            <span style={{ fontSize: '16px' }}>{currentLang?.flag}</span>
            <span className="text-xs text-gray-400 font-medium">{currentLang?.code?.toUpperCase()}</span>
          </button>
          <button onClick={() => { logout(); navigate("/admin"); }} className="btn-secondary text-sm py-2 px-4">Salir</button>
        </div>
      </header>
      {langPickerOpen && <LanguagePicker onClose={() => setLangPickerOpen(false)} />}

      {dbPersistenceWarning && (
        <div className="bg-yellow-500/15 border-b border-yellow-500/40 px-6 py-3 flex items-start gap-3">
          <AlertTriangle className="text-yellow-400 shrink-0 mt-0.5" size={18} />
          <div className="flex-1 min-w-0">
            <p className="text-yellow-300 text-sm font-semibold">Base de datos efímera — datos en riesgo</p>
            <p className="text-yellow-400/80 text-xs mt-0.5">
              La plataforma usa MongoDB local. <strong>Los datos se perderán en cada nuevo deployment.</strong> Configura la variable de entorno <code className="bg-black/30 px-1 rounded">MONGO_URL</code> con una cadena de conexión de MongoDB Atlas para persistencia permanente.
            </p>
          </div>
        </div>
      )}

      {dashboardError && (
        <div className="mx-6 mt-4 bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 flex items-center gap-3">
          <AlertTriangle className="text-red-400 shrink-0" size={18} />
          <div className="flex-1 min-w-0">
            <p className="text-red-300 text-sm font-semibold">Datos administrativos no disponibles</p>
            <p className="text-red-200/80 text-xs mt-0.5">{dashboardError}</p>
          </div>
          <button onClick={loadData} disabled={dataLoading} className="btn-secondary text-xs py-2 px-3 flex items-center gap-1.5">
            <RefreshCw size={13} className={dataLoading ? "animate-spin" : ""} />Reintentar
          </button>
        </div>
      )}

      <div className="px-6 py-4 border-b border-white/5 flex gap-2 overflow-x-auto no-scrollbar">
        {tabs.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setActiveTab(t.id)} className={`tab-button ${activeTab === t.id ? 'active' : 'inactive'}`}>
              <Icon size={16} />{t.label}
            </button>
          );
        })}
      </div>

      <div className="p-6">
        {activeTab === "overview" && metrics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: "Usuarios", value: metrics.users, icon: Users },
              { label: "Negocios", value: metrics.businesses, icon: Building2 },
              { label: "Pedidos", value: metrics.orders, icon: Package },
              { label: "Webhooks", value: metrics.webhooks, icon: Webhook },
            ].map((m, i) => {
              const Icon = m.icon;
              return (
                <div key={i} className="glass-panel p-5">
                  <div className="flex items-center gap-2 mb-2"><Icon size={18} className="text-red-400" /><span className="text-gray-500 text-sm">{m.label}</span></div>
                  <p className="text-3xl font-semibold text-white">{m.value}</p>
                </div>
              );
            })}
          </div>
        )}
        {activeTab === "overview" && !metrics && (
          <div className="glass-panel p-10 text-center">
            {dataLoading ? (
              <RefreshCw size={24} className="text-red-400 animate-spin mx-auto" />
            ) : (
              <>
                <AlertTriangle size={24} className="text-red-400 mx-auto mb-3" />
                <p className="text-gray-300">No se pudieron cargar las métricas administrativas.</p>
                <button onClick={loadData} className="btn-secondary mt-4 text-sm">Reintentar</button>
              </>
            )}
          </div>
        )}

        {activeTab === "users" && (
          <div>
            <div className="flex justify-between mb-6">
              <h2 className="text-xl font-semibold text-white">Usuarios</h2>
              <button onClick={() => setShowCreateUser(true)} className="btn-primary flex items-center gap-2"><Plus size={16} />Nuevo</button>
            </div>

            {showCreateUser && (
              <div className="glass-panel p-6 mb-6 animate-fade-in-up">
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <input type="text" value={newUser.first_name} onChange={(e) => setNewUser({ ...newUser, first_name: e.target.value })}
                         placeholder="Nombre(s)" className="input-glass" />
                  <input type="text" value={newUser.last_name} onChange={(e) => setNewUser({ ...newUser, last_name: e.target.value })}
                         placeholder="Apellidos" className="input-glass" />
                  <input type="text" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                         placeholder="Nombre de usuario completo *" className="input-glass" />
                  <input type="text" value={newUser.okuser} onChange={(e) => setNewUser({ ...newUser, okuser: e.target.value })}
                         placeholder="OkUser (único) *" className="input-glass" />
                  <input type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                         placeholder="Email (opcional)" className="input-glass" />
                  <input type="tel" value={newUser.whatsapp} onChange={(e) => setNewUser({ ...newUser, whatsapp: e.target.value })}
                         placeholder="WhatsApp +52 55..." className="input-glass" />
                </div>
                <div className="flex gap-3 justify-end">
                  <button onClick={() => setShowCreateUser(false)} className="btn-secondary">Cancelar</button>
                  <button onClick={createUser} className="btn-primary">Crear</button>
                </div>
              </div>
            )}

            <div className="glass-panel overflow-x-auto">
              <table className="table-glass">
                <thead><tr><th>Nombre</th><th>OkUser</th><th>PIN</th><th>Estado</th><th>Acciones</th></tr></thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id}>
                      <td className="text-white">{u.name}</td>
                      <td className="text-gray-500">{u.okuser}</td>
                      <td><span className={`badge ${u.pin_configured ? 'badge-success' : 'badge-warning'}`}>{u.pin_configured ? 'OK' : 'Pendiente'}</span></td>
                      <td><span className={`badge ${u.is_locked ? 'badge-error' : 'badge-success'}`}>{u.is_locked ? 'Bloqueado' : 'Activo'}</span></td>
                      <td className="flex gap-2 flex-wrap">
                        <button
                          onClick={() => axios.put(`${API}/admin/users/${u.id}/toggle-lock`).then(loadData)}
                          className={`text-xs px-2 py-1 rounded ${u.is_locked ? 'bg-green-600/30 text-green-400 hover:bg-green-600/50' : 'bg-red-600/30 text-red-400 hover:bg-red-600/50'}`}>
                          {u.is_locked ? 'Desbloquear' : 'Bloquear'}
                        </button>
                        <button
                          onClick={() => { if (window.confirm(`¿Resetear PIN de ${u.name}? Deberá configurar uno nuevo.`)) axios.put(`${API}/admin/users/${u.id}/reset-pin`).then(() => alert('PIN reiniciado')).catch(e => alert('Error: ' + e.message)); }}
                          className="text-xs px-2 py-1 rounded bg-yellow-600/30 text-yellow-400 hover:bg-yellow-600/50">
                          Resetear PIN
                        </button>
                        <button
                          onClick={() => deleteUser(u.id, u.name)}
                          className="text-xs px-2 py-1 rounded bg-red-800/40 text-red-300 hover:bg-red-700/60 flex items-center gap-1">
                          <Trash2 size={11} />Eliminar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === "businesses" && (
          <div>
            <div className="flex justify-between mb-6">
              <h2 className="text-xl font-semibold text-white">Negocios</h2>
              <button onClick={() => setShowCreateBusiness(true)} className="btn-primary flex items-center gap-2"><Plus size={16} />Nuevo</button>
            </div>

            {showCreateBusiness && (
              <div className="glass-panel p-6 mb-6 animate-fade-in-up">
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <input type="text" value={newBusiness.name} onChange={(e) => setNewBusiness({ ...newBusiness, name: e.target.value })}
                         placeholder="Nombre del negocio" className="input-glass" />
                  <input type="text" value={newBusiness.okbusiness} onChange={(e) => setNewBusiness({ ...newBusiness, okbusiness: e.target.value })}
                         placeholder="OkBusiness (único)" className="input-glass" />
                  <select value={newBusiness.category} onChange={(e) => setNewBusiness({ ...newBusiness, category: e.target.value })} className="input-glass">
                    <option value="store">Tienda</option>
                    <option value="restaurant">Restaurante</option>
                    <option value="mechanic">Mecánico</option>
                    <option value="other">Otro</option>
                  </select>
                </div>
                <div className="flex gap-3 justify-end">
                  <button onClick={() => setShowCreateBusiness(false)} className="btn-secondary">Cancelar</button>
                  <button onClick={createBusiness} className="btn-primary">Crear</button>
                </div>
              </div>
            )}

            <div className="glass-panel overflow-x-auto">
              <table className="table-glass">
                <thead><tr><th>Nombre</th><th>OkBusiness</th><th>Categoría</th><th>Estado</th><th>Acciones</th></tr></thead>
                <tbody>
                  {businesses.map(b => (
                    <tr key={b.id}>
                      <td className="text-white">{b.name}</td>
                      <td className="text-gray-500">{b.okbusiness}</td>
                      <td className="text-gray-500 capitalize">{b.category}</td>
                      <td><span className={`badge ${b.is_locked ? 'badge-error' : 'badge-success'}`}>{b.is_locked ? 'Bloqueado' : 'Activo'}</span></td>
                      <td className="flex gap-2 flex-wrap">
                        <button
                          onClick={() => axios.put(`${API}/admin/users/${b.id}/toggle-lock`).then(loadData)}
                          className={`text-xs px-2 py-1 rounded ${b.is_locked ? 'bg-green-600/30 text-green-400 hover:bg-green-600/50' : 'bg-red-600/30 text-red-400 hover:bg-red-600/50'}`}>
                          {b.is_locked ? 'Desbloquear' : 'Bloquear'}
                        </button>
                        <button
                          onClick={() => { if (window.confirm(`¿Resetear PIN de ${b.name}? Deberá configurar uno nuevo.`)) axios.put(`${API}/admin/users/${b.id}/reset-pin`).then(() => alert('PIN reiniciado')).catch(e => alert('Error: ' + e.message)); }}
                          className="text-xs px-2 py-1 rounded bg-yellow-600/30 text-yellow-400 hover:bg-yellow-600/50">
                          Resetear PIN
                        </button>
                        <button
                          onClick={() => deleteBusiness(b.id, b.name)}
                          className="text-xs px-2 py-1 rounded bg-red-800/40 text-red-300 hover:bg-red-700/60 flex items-center gap-1">
                          <Trash2 size={11} />Eliminar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === "okwallet" && (
          <OkWalletAdmin />
        )}

        {activeTab === "support" && (
          <AdminSupportPanel />
        )}

        {activeTab === "stripe" && (
          <StripeAdminPanel />
        )}

        {activeTab === "connect" && (
          <SuperAdminConnect />
        )}

        {activeTab === "agents" && (
          <AIAgentsManager />
        )}

        {activeTab === "integrations" && (
          <ExternalIntegrationsPanel />
        )}

        {activeTab === "knowledge-apis" && (
          <KnowledgeAPIsManager />
        )}

        {activeTab === "web-scraper" && (
          <div className="space-y-2">
            <h2 className="text-white font-bold text-lg mb-1">🌐 Web Scraper IA</h2>
            <p className="text-gray-500 text-sm mb-4">Extrae y analiza sitios web completos con IA. Profundidad configurable (max. 30 páginas).</p>
            <WebScraperTool />
          </div>
        )}

        {activeTab === "rapidapi" && (
          <RapidAPIManager />
        )}

        {activeTab === "oauth" && (
          <ErrorBoundary>
            <OAuthSSOSection />
          </ErrorBoundary>
        )}

        {activeTab === "okgo" && (
          <ErrorBoundary>
            <OkGoIntegrationPanel />
          </ErrorBoundary>
        )}

        {activeTab === "webhooks" && (
          <WebhooksManager />
        )}

        {activeTab === "api" && (
          <APIKeysDocsManager />
        )}

        {activeTab === "widgets" && <WidgetsManager />}

        {activeTab === "bubble" && bubbleDocs && <BubbleSection bubbleDocs={bubbleDocs} />}

        {activeTab === "legal" && <LegalSection />}

        {activeTab === "mcp" && <MCPManager />}

        {activeTab === "kapso" && <KapsoServerPanel />}

        {activeTab === "jelou" && <JelouPanel />}

        {activeTab === "fishaudio" && <FishAudioAdminPanel />}

        {activeTab === "okbook" && <OkbotAdminPanel />}

      </div>
    </div>
  );
};

// ==================== ROUTES ====================
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/" replace />;
  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
};

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <LanguageProvider>
        <BrowserRouter>
          <Toaster position="top-center" toastOptions={{ style: { background: '#1a1a1a', color: '#fff', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '14px' } }} />
          <Routes>
            <Route path="/" element={<UserLoginPage />} />
            <Route path="/superpoderes" element={<SuperPoderesPage />} />
            <Route path="/oknegocios" element={<BusinessLoginPage />} />
            <Route path="/admin" element={<AdminLoginPage />} />
            <Route path="/terminos" element={<TermsPage />} />
            <Route path="/privacidad" element={<PrivacyPage />} />
            <Route path="/aviso-legal" element={<LegalNoticePage />} />
            <Route path="/cookies" element={<CookiesPage />} />
            <Route path="/sso-callback" element={<SSOCallbackPage />} />
            <Route path="/embed/agent" element={<AgentEmbedPage />} />
            <Route path="/soporte" element={<SoportePage />} />
            <Route path="/music/spotify-callback" element={<SpotifyCallback />} />
            <Route path="/api-docs" element={<PublicAPIDocsPage />} />
            <Route path="/docs" element={<PublicAPIDocsPage />} />
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/business/dashboard" element={<ProtectedRoute allowedRoles={["business"]}><BusinessDashboard /></ProtectedRoute>} />
            <Route path="/admin/dashboard" element={<ProtectedRoute allowedRoles={["admin"]}><AdminDashboard /></ProtectedRoute>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        </LanguageProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}

export { useTheme };
export default App;
